import { Link } from 'react-router-dom';
import { useState } from 'react';
import usePageMeta from '../../hooks/usePageMeta';
import { ArrowRight, CircleDollarSign, Clock3, Award } from 'lucide-react';

// dev logger
const devLog = (...args) => {
  if (import.meta.env.MODE !== 'production') console.log(...args);
};

export default function StaffingJobSeekersPage() {
  usePageMeta({
    title: 'Job Opportunities – Stratel Staffing',
    description: 'Browse current healthcare job openings and apply to join the Stratel staffing network. Opportunities across long-term care and healthcare.',
    keywords: 'healthcare jobs, staffing jobs, long-term care careers, apply staffing',
  });
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', availability: '', coverLetter: '', consent: false, resume: null });
  const commonRoles = [
    'Registered Nurse (RN)',
    'Licensed Practical Nurse (LPN/LVN)',
    'Certified Nursing Assistant (CNA)',
    'Medication Technician',
    'Dietary Staff',
    'Caregiver / Resident Aide',
  ];

  function handleInputChange(e) {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    setForm((prev) => ({ ...prev, resume: file }));
  }

  function handleClose() {
    setShowModal(false);
    setSubmitting(false);
    setSubmitted(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email) {
      alert('Please provide your name and email.');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      if (form.phone) formData.append('phone', form.phone);
      if (form.role) formData.append('role', form.role);
      if (form.availability) formData.append('availability', form.availability);
      if (form.coverLetter) formData.append('coverLetter', form.coverLetter);
      formData.append('consent', form.consent ? 'true' : 'false');
      if (form.resume) {
        formData.append('resume', form.resume, form.resume.name);
      }

      // Use the axios instance (default export) to POST multipart/form-data
      const api = (await import('../../services/api')).default;
      const res = await api.post('/staffing/applicants', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitted(true);
      setShowModal(false);
      // Optionally notify admins or show reference id
      devLog('Application submitted', res.data);
      // reset form
      setForm({ name: '', email: '', phone: '', role: '', availability: '', coverLetter: '', consent: false, resume: null });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Submission failed';
      alert(`Submission failed: ${msg}`);
      if (import.meta.env.MODE !== 'production') console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-700 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-emerald-500/30 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                <span className="text-sm font-semibold text-emerald-100">Expert Talent Solutions for Job Seekers</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Grow Your Career With Stratel
              </h1>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed max-w-2xl">
                Are you seeking a supplemental source of income? Join our network of healthcare professionals and get access to curated short-term opportunities, tailored matching, and responsive support.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center justify-center bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Join Our Pool
                  <ArrowRight size={20} className="ml-2" />
                </button>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300"
                >
                  Contact Us
                </Link>
              </div>
            </div>

            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <p className="text-emerald-100 italic">
                  We review and match candidate profiles with relevant roles and provide timely updates throughout the process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
          <div className="relative bg-white rounded-xl w-full max-w-2xl p-6 shadow-xl z-60 max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Join Our Staffing Pool</h3>
              <button onClick={handleClose} className="text-secondary-600">Close</button>
            </div>

            {submitted ? (
              <div className="p-6">
                <p className="mb-4">Thanks — your application has been received. We'll be in touch soon.</p>
                <div className="text-right">
                  <button onClick={handleClose} className="inline-flex items-center justify-center bg-emerald-600 text-white px-4 py-2 rounded-lg">Close</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field for spam protection - leave hidden */}
                <input type="text" name="hp" value={form.hp || ''} onChange={(e) => setForm(prev => ({...prev, hp: e.target.value}))} className="hidden" autoComplete="off" tabIndex={-1} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="name" value={form.name} onChange={handleInputChange} placeholder="Full name" className="border px-3 py-2 rounded w-full" />
                  <input name="email" type="email" value={form.email} onChange={handleInputChange} placeholder="Email" className="border px-3 py-2 rounded w-full" />
                  <input name="phone" value={form.phone} onChange={handleInputChange} placeholder="Phone" className="border px-3 py-2 rounded w-full" />
                  <input name="role" value={form.role} onChange={handleInputChange} placeholder="Desired role/title" className="border px-3 py-2 rounded w-full" />
                </div>

                <textarea name="coverLetter" value={form.coverLetter} onChange={handleInputChange} placeholder="Short message or cover note" className="border px-3 py-2 rounded w-full" rows="4"></textarea>

                <div>
                  <label className="block mb-1 text-sm">Resume (optional)</label>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
                  {form.resume && (<div className="text-sm mt-1">Selected: {form.resume.name}</div>)}
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={handleInputChange} />
                  <label className="text-sm">I consent to Stratel storing and processing my application.</label>
                </div>

                <div className="flex justify-end gap-2">
                  <button type="button" onClick={handleClose} className="px-4 py-2 rounded border">Cancel</button>
                  <button type="submit" className="px-4 py-2 rounded bg-emerald-600 text-white" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Application'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Benefits */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-secondary-900 mb-12">Why Join the Stratel Pool?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-md p-8 border border-secondary-100 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center text-emerald-600">
              <CircleDollarSign className="w-7 h-7" />
            </div>
            <h3 className="font-bold text-lg mb-2">Competitive Pay</h3>
            <p className="text-secondary-600">We work with clients to offer fair, competitive compensation and transparent pay models.</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8 border border-secondary-100 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center text-emerald-600">
              <Clock3 className="w-7 h-7" />
            </div>
            <h3 className="font-bold text-lg mb-2">Flexible Shifts</h3>
            <p className="text-secondary-600">Pick assignments that match your availability — short-term, long-term, per diem, and more.</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8 border border-secondary-100 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center text-emerald-600">
              <Award className="w-7 h-7" />
            </div>
            <h3 className="font-bold text-lg mb-2">Professional Development</h3>
            <p className="text-secondary-600">Opportunities for training, credentialing support, and career growth through placements.</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-br from-emerald-50 to-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-secondary-900 mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[{step:1,title:'Apply',desc:'Complete a short application and share your resume and preferences.'},{step:2,title:'Profile Review',desc:'We verify credentials and match your skills to open roles.'},{step:3,title:'Interview & Onboard',desc:'Complete a brief interview and onboarding where required.'},{step:4,title:'Get Matched',desc:'We connect you with roles that fit your availability and goals.'}].map((s) => (
              <div key={s.step} className="bg-white rounded-xl p-8 shadow-md text-center border border-secondary-100">
                <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold mx-auto mb-4">{s.step}</div>
                <h4 className="font-semibold mb-2">{s.title}</h4>
                <p className="text-sm text-secondary-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Staff Roles Available */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-secondary-900">Staff Roles Available</h2>
          <button onClick={() => setShowModal(true)} className="text-sm text-emerald-600 hover:underline">Join Our Pool</button>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border border-secondary-100">
          <p className="text-secondary-600 mb-4">
            Common roles we frequently place:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {commonRoles.map((role) => (
              <div
                key={role}
                className="px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm font-medium border border-emerald-100"
              >
                {role}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* FAQs */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-secondary-900 mb-12">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold mb-2">Are assignments temporary or permanent?</h4>
              <p className="text-secondary-600 mb-4">Most assignments are temporary, short-term opportunities based on client demand.</p>

              <h4 className="font-semibold mb-2">How often will I receive shift offers?</h4>
              <p className="text-secondary-600 mb-4">After you are accepted into the pool, you set your weekly availability. Shift assignments are shared based on your availability, role fit, and active client demand.</p>

              <h4 className="font-semibold mb-2">What if there are no client requests?</h4>
              <p className="text-secondary-600 mb-4">If there are no active client requests, there are no shifts available.</p>

              <h4 className="font-semibold mb-2">Do I choose which shifts to accept?</h4>
              <p className="text-secondary-600">Yes. In addition to matched assignments, you can also accept unmatched shifts that fit your stated availability.</p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">What credentials do I need before placement?</h4>
              <p className="text-secondary-600 mb-4">Required credentials depend on the role. We review and verify role-specific licenses and certifications during onboarding.</p>

              <h4 className="font-semibold mb-2">How and when do I get paid?</h4>
              <p className="text-secondary-600 mb-4">Payment timing and method are shared during onboarding and assignment confirmation.</p>

              <h4 className="font-semibold mb-2">What if a shift is changed or canceled?</h4>
              <p className="text-secondary-600">We communicate updates as quickly as possible and coordinate next steps with you.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Join?</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto leading-relaxed">Complete a short application and we'll find opportunities that match your skills and availability.</p>
          <div className="flex justify-center gap-4">
            <button onClick={() => setShowModal(true)} className="inline-flex items-center justify-center bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl">Join Our Pool <ArrowRight size={20} className="ml-2" /></button>
            <Link to="/contact" className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}



